import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Stepper, Step, StepLabel, StepConnector, stepConnectorClasses} from 'views/components';
import {Card, CardContent, Typography, Stack, Box, Button, screenSizeSmaller} from 'views/components';
import {Assignment, GroupOutlined, FactCheckOutlined, CalendarMonth, Check, EventAvailable, PhoneOutlined} from 'views/icons';
import {Translate, translate} from 'views/module';

import buildBlakeCalendlyLink from '../../utils/blakeCalendlyLink';

import moment from 'moment';

const NextStepsSection = (props) => {
  const {user, clientData} = props;

  const buttonHref = React.useMemo(() => {
    return buildBlakeCalendlyLink({user, clientData});
  },
  [
    user?.userRn,
    user?.email,
    user?.firstName,
    user?.lastName,
    clientData?.clientDetail?.relationships?.[user?.userRn]?.contacts,
  ],
  );

  const caregivers = clientData?.clientDetail?.caregivers;
  const hasCaregivers = caregivers && Object.keys(caregivers).length > 0;
  // Do not show this section if there are active caregivers
  const hasActiveCaregiver = caregivers && Object.values(caregivers).some((c) => c.status === 'ACTIVE');
  if (hasActiveCaregiver) return null;

  // Start building steps
  const introCallStatus = clientData?.callStatus?.introCallStatus ?? 'NOT_SCHEDULED'; // null/undefined -> fallback to 'NOT_SCHEDULED'
  const scheduledCallTimeIso = clientData?.callStatus?.callDetail?.scheduledCallTime;
  const scheduledMoment = scheduledCallTimeIso ? moment(scheduledCallTimeIso) : null;
  const isValidScheduledCallTime = scheduledMoment?.isValid();
  const scheduledCallTimeFormatted = isValidScheduledCallTime ?
                                      scheduledMoment.format(translate('timeFormat.SimpleTime')) :
                                      translate('dashboard.sections.nextSteps.steps.meetManager.upcoming');
  const step1 = {};
  if (introCallStatus === 'NOT_SCHEDULED') {
    step1.icon = <Assignment />;
    step1.subtitle = translate('dashboard.sections.nextSteps.steps.book.subtitle');
    step1.title = translate('dashboard.sections.nextSteps.steps.book.title');
    step1.buttonIcon = <PhoneOutlined />;
    step1.buttonText = translate('dashboard.sections.nextSteps.steps.book.buttonText');
    step1.isActive = true;
    step1.onClick = () => {
      window.open(buttonHref, '_blank', 'noopener,noreferrer'); // Open link in a new tab
    };
  } else {
    step1.subtitle = isValidScheduledCallTime ?
                      translate('dashboard.sections.nextSteps.steps.meetManager.subtitle') :
                      translate('dashboard.sections.nextSteps.steps.meetManager.altSubtitle');
    step1.icon = <Assignment />;
    step1.title = translate('dashboard.sections.nextSteps.steps.meetManager.title');
    step1.buttonIcon = <EventAvailable />;
    step1.buttonText = scheduledCallTimeFormatted;
    step1.isActive = true;
  }

  const step2 = {
    icon: <GroupOutlined />,
    title: translate('dashboard.sections.nextSteps.steps.review.title'),
    buttonIcon: <FactCheckOutlined />,
    buttonText: translate('dashboard.sections.nextSteps.steps.review.buttonText'),
    subtitle: translate(hasCaregivers ?
                      'dashboard.sections.nextSteps.steps.review.altSubtitle' :
                      'dashboard.sections.nextSteps.steps.review.subtitle',
    ),
    isActive: hasCaregivers,
    href: '/caregivers',
  };

  const steps = [
    step1,
    step2,
    {
      icon: <Check />,
      title: translate('dashboard.sections.nextSteps.steps.start.title'),
      subtitle: translate('dashboard.sections.nextSteps.steps.start.subtitle'),
      buttonText: translate('dashboard.sections.nextSteps.steps.start.buttonText'),
      buttonIcon: <CalendarMonth />,
    },
  ];

  const isSmallScreen = screenSizeSmaller('md');

  return (
    <Stack spacing={2} id="next-steps">
      <Stack spacing={1}>
        <Typography
          variant='h5'
        >
          <Translate expression={'dashboard.sections.nextSteps.title'} />
        </Typography>
      </Stack>
      <Card>
        <CardContent>
          <Stepper
            alternativeLabel={!isSmallScreen}
            activeStep={-1}
            orientation={isSmallScreen ? 'vertical' : 'horizontal'}
            connector= {
              <StepConnector sx={{[`& .${stepConnectorClasses.line}`]: {margin: 12}}} />
            }
          >
            {
              steps.map((step) => (
                <Step key={step.title} disabled={!step.isActive}>
                  <StepLabel
                    slots={{
                      stepIcon: () => (
                        <Box
                          sx={{
                            width: 52,
                            height: 52,
                            borderRadius: '50%',
                            display: 'grid',
                            placeItems: 'center',
                            backgroundColor: step.isActive ? 'primary.main' : 'primary.light',
                            color: step.isActive ? 'white' : 'text.secondary',
                          }}
                        >
                          {step.icon}
                        </Box>
                      ),
                    }}
                  >
                    <Stack spacing={0.5} alignItems="center">
                      <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                        {step.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {step.subtitle}
                      </Typography>
                      <Button
                        variant='outlined'
                        startIcon={step.buttonIcon}
                        disabled={!step.isActive}
                        href={step.href}
                        onClick={step.onClick}
                      >
                        {step.buttonText}
                      </Button>
                    </Stack>
                  </StepLabel>
                </Step>
              ))}
          </Stepper>
        </CardContent>
      </Card>
    </Stack>
  );
};

NextStepsSection.propTypes = {
  user: PropTypes.object.isRequired,
  clientData: PropTypes.object.isRequired,
};


const mapStateToProps = (state) => ({
  user: state.user,
  clientData: state.clientData,
});

export default connect(mapStateToProps, dispatch)(NextStepsSection);
